import { useCallback, useState } from 'react';
import { useQueryParams } from './useQueryParams';
import { useSearchParams } from 'react-router-dom';

/**
 * A custom hook that syncs state with a URL search parameter.
 * Supports string, number, boolean, and object values.
 * @param key The search parameter key to sync with.
 * @param defaultValue The default value for the state.
 * @returns A stateful value, and a function to update it.
 */
function useParamState<T extends string | number | boolean | string[] | number[]>(
  key: string,
  defaultValue: T,
): [T, (newValue: Partial<T> | T) => void] {
  const [_, setSearchParams] = useSearchParams();
  const searchParams: URLSearchParams = useQueryParams();
  const paramValue = searchParams.get(key);

  const [state, setState] = useState<T>(() => {
    if (paramValue === null) {
      return defaultValue;
    }
    try {
      return JSON.parse(paramValue) as T;
    } catch {
      return paramValue as T;
    }
  });

  const setParamState = useCallback(
    (newValue: Partial<T> | T) => {

      setState(newValue as T);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete(key)
      newSearchParams.set(key, newValue.toString!());
      setSearchParams(newSearchParams);
    },
    [key, searchParams, setSearchParams, state]
  );

  return [state, setParamState];
}

export default useParamState;